import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../../contexts/AuthContext';
import { lightStyles } from '../common/OrgSelect';
import { fetchProxyOrganizations, PROXYABLE_ROLES } from '../../services/demoProxyService';
import { UserRole } from '../../types';
import { createLogger } from '../../utils/logger';
import './DemoProxySwitcher.scss';

type OrgOption = { value: string; label: string };

const SEARCH_DEBOUNCE_MS = 300;

// lightStyles sets a z-index on `menu`, but a portalled menu is positioned by
// `menuPortal` instead — without this it renders behind the header dropdown.
const selectStyles = {
  ...lightStyles,
  menuPortal: (base: any) => ({ ...base, zIndex: 2200 }),
};

const logger = createLogger('DemoProxySwitcher');

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrator',
  trainer: 'Trainer',
  trainee: 'Trainee',
};

interface DemoProxySwitcherProps {
  /** Called after a successful switch so the parent dropdown can close itself. */
  onSwitched?: () => void;
}

/**
 * "View as" control for demos — lets a super admin act as a lower role inside a
 * chosen organization without a second account.
 *
 * Rendered inside the user dropdown, and only for real super admins. The proxy is
 * server-side, so a switch is a round trip plus an auth refresh; the whole app then
 * behaves as the demonstrated role, backend included.
 */
const DemoProxySwitcher: React.FC<DemoProxySwitcherProps> = ({ onSwitched }) => {
  const { realRole, isProxyActive, userRole, userData, startDemoProxy, stopDemoProxy } = useAuth();

  const [role, setRole] = useState<UserRole>('trainee');
  const [org, setOrg] = useState<OrgOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = realRole === 'super_admin';
  const orgId = org?.value ?? '';

  // Pre-select whatever is being demoed now, so re-opening the menu mid-demo shows the
  // current state rather than resetting. The org's name comes from the user payload
  // (GET /user/{uid} mirrors the proxy), so no extra lookup is needed to label it.
  useEffect(() => {
    if (!isProxyActive) return;
    if (userRole) setRole(userRole);
    const activeOrgId = userData?.organization_id;
    if (activeOrgId) {
      setOrg({ value: activeOrgId, label: userData?.organization?.name || activeOrgId });
    }
  }, [isProxyActive, userRole, userData?.organization_id, userData?.organization?.name]);

  // Debounced server-side search. The org list is platform-wide and can run to
  // thousands, so it is never loaded whole — `defaultOptions` fetches one page for the
  // initial dropdown and each keystroke re-queries.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The previous call's `resolve`, if a newer keystroke supersedes it before its
  // timeout fires — clearing the timeout alone leaves that promise (already handed to
  // react-select) pending forever, stranding the control in a loading state.
  const pendingResolveRef = useRef<((options: OrgOption[]) => void) | null>(null);

  const loadOptions = useCallback(
    (search: string): Promise<OrgOption[]> =>
      new Promise((resolve) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        pendingResolveRef.current?.([]);
        pendingResolveRef.current = resolve;
        debounceRef.current = setTimeout(async () => {
          pendingResolveRef.current = null;
          try {
            const items = await fetchProxyOrganizations(search);
            resolve(items.map((o) => ({ value: o.id, label: o.name })));
          } catch (e) {
            logger.error('Failed to load organizations for the demo proxy', e);
            setError('Could not load organizations.');
            resolve([]);
          }
        }, SEARCH_DEBOUNCE_MS);
      }),
    [],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      pendingResolveRef.current?.([]);
    },
    [],
  );

  const handleStart = useCallback(async () => {
    if (!orgId) {
      setError('Pick an organization first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await startDemoProxy(role, orgId);
      onSwitched?.();
    } catch (e: any) {
      logger.error('Failed to start the demo proxy', e);
      setError(e?.message || 'Could not switch role.');
    } finally {
      setBusy(false);
    }
  }, [orgId, role, startDemoProxy, onSwitched]);

  const handleStop = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await stopDemoProxy();
      onSwitched?.();
    } catch (e: any) {
      logger.error('Failed to end the demo proxy', e);
      setError(e?.message || 'Could not restore your role.');
    } finally {
      setBusy(false);
    }
  }, [stopDemoProxy, onSwitched]);

  if (!isSuperAdmin) return null;

  return (
    <div className="demo-proxy-switcher" data-testid="demo-proxy-switcher">
      <div className="demo-proxy-switcher__title">
        <Eye size={14} />
        View as
      </div>

      <label className="demo-proxy-switcher__label" htmlFor="demo-proxy-role">
        Role
      </label>
      <select
        id="demo-proxy-role"
        className="demo-proxy-switcher__select"
        value={role}
        disabled={busy}
        onChange={(e) => setRole(e.target.value as UserRole)}
      >
        {PROXYABLE_ROLES.map((r) => (
          <option key={r} value={r}>
            {ROLE_LABELS[r]}
          </option>
        ))}
      </select>

      <label className="demo-proxy-switcher__label" htmlFor="demo-proxy-org">
        Organization
      </label>
      <div className="demo-proxy-switcher__org">
        <AsyncSelect<OrgOption>
          inputId="demo-proxy-org"
          aria-label="Organization"
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          value={org}
          onChange={(opt) => setOrg(opt ?? null)}
          isDisabled={busy}
          styles={selectStyles}
          placeholder="Search organizations..."
          noOptionsMessage={({ inputValue }) =>
            inputValue ? 'No matching organizations' : 'No organizations available'
          }
          // The header dropdown clips overflow, so the menu has to escape it.
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>

      {error && <p className="demo-proxy-switcher__error">{error}</p>}

      <div className="demo-proxy-switcher__actions">
        <button
          type="button"
          className="demo-proxy-switcher__button demo-proxy-switcher__button--primary"
          onClick={handleStart}
          disabled={busy || !orgId}
        >
          {isProxyActive ? 'Switch' : 'Start demo view'}
        </button>
        {isProxyActive && (
          <button
            type="button"
            className="demo-proxy-switcher__button"
            onClick={handleStop}
            disabled={busy}
          >
            Exit
          </button>
        )}
      </div>

      <p className="demo-proxy-switcher__hint">
        Your account and sign-in stay the same. Only the role and organization the app
        treats you as change.
      </p>
    </div>
  );
};

export default DemoProxySwitcher;
